/**
 * DocumentEditor — shared TipTap editor for SOP, Synopsis, and Research Paper.
 *
 * Props:
 *   documentId   string | null  — existing doc id (null while generating)
 *   initialContent string       — HTML to load on mount
 *   placeholder  string
 *   targetWords  number
 *   onSaved      (docId) => void — called after every successful save
 *
 * The component drives its own save/auto-save cycle and export actions.
 * Pass `documentId` once generation resolves so saves can persist.
 */

import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react'
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import Highlight from '@tiptap/extension-highlight'
import Link from '@tiptap/extension-link'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import Placeholder from '@tiptap/extension-placeholder'
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, Quote, Minus, Table as TableIcon,
  Undo2, Redo2, Save, FileDown, Sparkles, Loader2,
} from 'lucide-react'
import api from '../../lib/api'
import toast from 'react-hot-toast'

// ─── Editor CSS ───────────────────────────────────────────────────────────────

const EDITOR_STYLES = `
.doc-editor {
  outline: none;
  caret-color: #1A1410;
}
.doc-editor p { margin-bottom: 1em; }
.doc-editor h1 {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.5rem;
  font-weight: 700;
  text-align: center;
  margin: 2.5rem 0 1.5rem;
  color: #1A1410;
}
.doc-editor h2 {
  font-family: 'Source Serif 4', Georgia, serif;
  font-size: 1.125rem;
  font-weight: 700;
  margin: 2rem 0 0.75rem;
  color: #1A1410;
}
.doc-editor h3 {
  font-family: 'Source Serif 4', Georgia, serif;
  font-size: 1rem;
  font-weight: 700;
  font-style: italic;
  margin: 1.5rem 0 0.5rem;
  color: #1A1410;
}
.doc-editor h4 {
  font-family: 'Source Serif 4', Georgia, serif;
  font-size: 0.95rem;
  font-weight: 700;
  margin: 1.25rem 0 0.5rem;
  color: #1A1410;
}
.doc-editor ul, .doc-editor ol { padding-left: 1.5rem; margin-bottom: 1em; }
.doc-editor li { margin-bottom: 0.25em; }
.doc-editor blockquote {
  border-left: 3px solid #2563eb;
  padding-left: 1rem;
  margin: 1rem 0;
  font-style: italic;
  color: #4A3820;
}
.doc-editor table {
  width: 100%;
  border-collapse: collapse;
  margin: 1.5rem 0;
  font-size: 0.9rem;
}
.doc-editor td, .doc-editor th {
  border: 1px solid #c5b89a;
  padding: 0.5rem 0.75rem;
}
.doc-editor th { background: #ede8df; font-weight: 600; }
.doc-editor p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #b0a898;
  pointer-events: none;
  height: 0;
}
.doc-editor mark { background: rgba(37,99,235,0.25); }
`

// ─── Toolbar helpers ──────────────────────────────────────────────────────────

function TBtn({ onClick, active, disabled, children, title, gold = false }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className="flex items-center justify-center w-7 h-7 rounded transition-colors disabled:opacity-40"
      style={{
        background: active ? (gold ? 'rgba(37,99,235,0.15)' : 'rgba(12,22,41,0.08)') : 'transparent',
        color: active ? (gold ? '#2563eb' : '#0C1629') : gold ? '#1d4ed8' : '#8EA4BC',
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(12,22,41,0.05)' }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent' }}
    >
      {children}
    </button>
  )
}

function TSep() {
  return <div className="w-px h-5 mx-1 shrink-0" style={{ background: '#DDE4EF' }} />
}

// ─── Toolbar ──────────────────────────────────────────────────────────────────

function DocToolbar({ editor, wordCount, saving, onSave, onTransform, transforming }) {
  if (!editor) return null

  const H = ({ level }) => (
    <TBtn
      onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
      active={editor.isActive('heading', { level })}
      title={`Heading ${level}`}
    >
      <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'JetBrains Mono' }}>H{level}</span>
    </TBtn>
  )

  return (
    <div
      className="sticky top-0 z-20 flex items-center gap-0.5 px-4 overflow-x-auto shrink-0"
      style={{ background: '#FFFFFF', borderBottom: '1px solid #DDE4EF', height: 52, minWidth: 0 }}
    >
      <H level={1} /><H level={2} /><H level={3} /><H level={4} />
      <TSep />
      <TBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold">
        <Bold style={{ width: 13, height: 13 }} />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic">
        <Italic style={{ width: 13, height: 13 }} />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline">
        <UnderlineIcon style={{ width: 13, height: 13 }} />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough">
        <Strikethrough style={{ width: 13, height: 13 }} />
      </TBtn>
      <TSep />
      <TBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list">
        <List style={{ width: 13, height: 13 }} />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered list">
        <ListOrdered style={{ width: 13, height: 13 }} />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote">
        <Quote style={{ width: 13, height: 13 }} />
      </TBtn>
      <TSep />
      <TBtn onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insert table">
        <TableIcon style={{ width: 13, height: 13 }} />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal rule">
        <Minus style={{ width: 13, height: 13 }} />
      </TBtn>
      <TSep />
      {['expand', 'simplify', 'rephrase'].map(mode => (
        <TBtn key={mode} gold onClick={() => onTransform(mode)} disabled={transforming} title={mode.charAt(0).toUpperCase() + mode.slice(1)}>
          <span style={{ fontSize: 10, fontWeight: 600, fontFamily: 'Sora', textTransform: 'capitalize' }}>
            {transforming ? '…' : mode === 'expand' ? '↕' : mode === 'simplify' ? '↓' : '↺'}
          </span>
        </TBtn>
      ))}
      <TSep />
      <TBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
        <Undo2 style={{ width: 13, height: 13 }} />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
        <Redo2 style={{ width: 13, height: 13 }} />
      </TBtn>

      <div className="ml-auto flex items-center gap-3 shrink-0">
        <span className="font-mono text-xs text-acdyon-textFaint">{wordCount.toLocaleString()} words</span>
        <button
          onClick={onSave}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-sans text-xs font-medium transition-all"
          style={{
            background: saving ? 'rgba(61,184,122,0.1)' : 'rgba(37,99,235,0.1)',
            color: saving ? '#3DB87A' : '#2563eb',
            border: `1px solid ${saving ? 'rgba(61,184,122,0.3)' : 'rgba(37,99,235,0.3)'}`,
          }}
        >
          <Save style={{ width: 12, height: 12 }} />
          {saving ? 'Saved' : 'Save'}
        </button>
      </div>
    </div>
  )
}

// ─── Floating AI menu ─────────────────────────────────────────────────────────

function FloatingMenu({ editor, onTransform, transforming }) {
  if (!editor) return null
  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{ duration: 100 }}
      shouldShow={({ from, to }) => from !== to}
    >
      <div
        className="flex items-center gap-0.5 px-2 py-1 rounded-full"
        style={{ background: '#FFFFFF', border: '1px solid rgba(37,99,235,0.3)', boxShadow: '0 4px 16px rgba(12,22,41,0.12)' }}
      >
        {['expand', 'simplify', 'rephrase'].map(mode => (
          <button
            key={mode}
            type="button"
            onClick={() => onTransform(mode)}
            disabled={transforming}
            className="flex items-center gap-1 px-2.5 py-1 rounded-full font-sans text-xs font-medium transition-colors disabled:opacity-50"
            style={{ color: '#2563eb' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(37,99,235,0.1)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            <Sparkles style={{ width: 10, height: 10 }} />
            {mode.charAt(0).toUpperCase() + mode.slice(1)}
          </button>
        ))}
        <div className="w-px h-4 mx-1" style={{ background: '#DDE4EF' }} />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          className="flex items-center gap-1 px-2.5 py-1 rounded-full font-sans text-xs font-medium transition-colors"
          style={{ color: '#506280' }}
          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(12,22,41,0.05)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          Highlight
        </button>
      </div>
    </BubbleMenu>
  )
}

// ─── Right panel ──────────────────────────────────────────────────────────────

function RightPanel({ documentId, wordCount, targetWords, saving, lastSaved, docTitle }) {
  const pct   = targetWords > 0 ? Math.min(Math.round((wordCount / targetWords) * 100), 100) : 0
  const pages = Math.round(wordCount / 250)
  const [exporting, setExporting] = useState(null)

  const exportFile = async (format) => {
    if (!documentId) return
    setExporting(format)
    try {
      const res = await api.post(`/documents/${documentId}/export/${format}`, {}, { responseType: 'blob' })
      const url = URL.createObjectURL(res.data)
      const a   = document.createElement('a')
      a.href     = url
      a.download = `${(docTitle || 'document').replace(/[^a-z0-9]/gi, '_')}.${format}`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Export error:', err)
      toast.error('Failed to export document')
    } finally {
      setExporting(null)
    }
  }

  return (
    <div
      className="flex flex-col shrink-0 overflow-y-auto"
      style={{ width: 280, background: '#FFFFFF', borderLeft: '1px solid #DDE4EF' }}
    >
      {/* Word count */}
      <div className="p-4 space-y-4" style={{ borderBottom: '1px solid #DDE4EF' }}>
        <div>
          <div className="flex items-baseline justify-between mb-1">
            <span className="font-mono text-2xl font-semibold text-brand-600">{wordCount.toLocaleString()}</span>
            {targetWords > 0 && (
              <span className="font-sans text-xs text-acdyon-textFaint">/ {targetWords.toLocaleString()}</span>
            )}
          </div>
          {targetWords > 0 && (
            <>
              <div className="h-1.5 rounded-full overflow-hidden mb-1" style={{ background: '#DDE4EF' }}>
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${pct}%`, background: pct >= 100 ? '#3DB87A' : '#2563eb' }}
                />
              </div>
              <div className="flex justify-between">
                <span className="font-sans text-xs text-acdyon-textFaint">{pct}% of target</span>
                <span className="font-mono text-xs text-acdyon-textFaint">{pages} pp</span>
              </div>
            </>
          )}
        </div>

        {/* Save status */}
        <div className="flex items-center gap-1.5">
          {saving ? (
            <>
              <span className="w-2 h-2 rounded-full bg-brand-600 animate-pulse" />
              <span className="font-sans text-xs text-acdyon-textFaint">Saving…</span>
            </>
          ) : lastSaved ? (
            <>
              <span className="w-2 h-2 rounded-full" style={{ background: '#3DB87A' }} />
              <span className="font-sans text-xs text-acdyon-textFaint">Saved {lastSaved}</span>
            </>
          ) : null}
        </div>
      </div>

      {/* Export */}
      <div className="p-4 space-y-2" style={{ borderBottom: '1px solid #DDE4EF' }}>
        <p className="font-sans text-xs font-semibold text-acdyon-textFaint uppercase tracking-wide mb-3">Export</p>
        {['docx', 'pdf'].map(fmt => (
          <button
            key={fmt}
            onClick={() => exportFile(fmt)}
            disabled={!documentId || exporting === fmt}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg font-sans text-sm transition-colors disabled:opacity-40"
            style={{ border: '1px solid #DDE4EF', color: '#506280' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#0C1629'; e.currentTarget.style.borderColor = 'rgba(37,99,235,0.3)' }}
            onMouseLeave={e => { e.currentTarget.style.color = '#506280'; e.currentTarget.style.borderColor = '#DDE4EF' }}
          >
            {exporting === fmt ? (
              <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
            ) : (
              <FileDown style={{ width: 14, height: 14 }} />
            )}
            {fmt === 'docx' ? 'Export as Word (.docx)' : 'Export as PDF'}
          </button>
        ))}
        {!documentId && (
          <p className="font-sans text-xs text-acdyon-textFaint mt-2">
            Generate a document first to enable export.
          </p>
        )}
      </div>
    </div>
  )
}

// ─── DocumentEditor (exported) ────────────────────────────────────────────────

const DocumentEditor = forwardRef(function DocumentEditor({
  documentId = null,
  initialContent = '',
  placeholder = 'Your document begins here…',
  targetWords = 0,
  docTitle = '',
  streaming = false,
  streamingLabel = 'Generating…',
  onSaved = () => {},
  onStopStreaming = null,
}, ref) {
  const [wordCount,    setWordCount]    = useState(0)
  const [saving,       setSaving]       = useState(false)
  const [lastSaved,    setLastSaved]    = useState('')
  const [transforming, setTransforming] = useState(false)
  const saveTimerRef = useRef(null)
  const docIdRef     = useRef(documentId)

  // Keep docIdRef current so callbacks don't close over stale value
  useEffect(() => { docIdRef.current = documentId }, [documentId])

  // ── Editor ─────────────────────────────────────────────────────────────

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Highlight,
      Link.configure({ openOnClick: false }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      Placeholder.configure({ placeholder }),
    ],
    content: initialContent || '',
    editorProps: {
      attributes: { class: 'doc-editor', spellcheck: 'true' },
    },
    onUpdate: ({ editor }) => {
      const text = editor.getText()
      setWordCount(text.trim() ? text.trim().split(/\s+/).length : 0)
      scheduleSave()
    },
  })

  // ── Load initial content into editor ───────────────────────────────────

  useEffect(() => {
    if (editor && initialContent) {
      editor.commands.setContent(initialContent)
      const text = editor.getText()
      setWordCount(text.trim() ? text.trim().split(/\s+/).length : 0)
    }
  }, [editor, initialContent]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save ────────────────────────────────────────────────────────────────

  const scheduleSave = useCallback(() => {
    clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => save(), 2000)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const save = useCallback(async () => {
    const id = docIdRef.current
    if (!editor || !id) return
    setSaving(true)
    try {
      const content  = editor.getHTML()
      const wc       = editor.getText().trim().split(/\s+/).filter(Boolean).length
      await api.put(`/documents/${id}`, { content, word_count: wc })
      setLastSaved('just now')
      onSaved(id)
    } catch { /* silent */ } finally {
      setSaving(false)
    }
  }, [editor, onSaved])

  // Cmd+S shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') { e.preventDefault(); save() }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [save])

  // Warn on unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (saveTimerRef.current) { e.preventDefault(); e.returnValue = '' }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [])

  // ── Inline transform ────────────────────────────────────────────────────

  const handleTransform = async (mode) => {
    if (!editor || !docIdRef.current) return
    const { from, to } = editor.state.selection
    if (from === to) return
    const selectedText = editor.state.doc.textBetween(from, to, ' ')
    if (!selectedText.trim()) return

    setTransforming(true)
    try {
      const { data } = await api.post(`/documents/${docIdRef.current}/inline-transform`, {
        mode,
        selectedText,
        context: `Document: ${docTitle}.`,
      })
      if (data.result) {
        editor.chain().focus().deleteRange({ from, to }).insertContentAt(from, data.result).run()
      }
    } catch { /* silent */ } finally {
      setTransforming(false)
    }
  }

  // ── Expose methods via ref for streaming ───────────────────────────────

  useImperativeHandle(ref, () => ({
    insertChunk: (text) => { if (editor) editor.commands.insertContent(text) },
    clearContent: ()    => { if (editor) editor.commands.clearContent() },
    getHTML:      ()    => editor?.getHTML() ?? '',
    setHTML:      (html) => { if (editor) editor.commands.setContent(html, false) },
  }), [editor])

  return (
    <>
      <style>{EDITOR_STYLES}</style>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div className="flex" style={{ height: 'calc(100vh - 4rem)', overflow: 'hidden' }}>

        {/* Centre — editor */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden" style={{ background: '#FFFFFF' }}>
          <DocToolbar
            editor={editor}
            wordCount={wordCount}
            saving={saving}
            onSave={save}
            onTransform={handleTransform}
            transforming={transforming}
          />

          <FloatingMenu editor={editor} onTransform={handleTransform} transforming={transforming} />

          {/* Parchment scroll area */}
          <div className="flex-1 overflow-y-auto" style={{ background: '#F0F4FA' }}>
            <div
              className="mx-auto my-8"
              style={{
                maxWidth: 860,
                background: '#F5F0E8',
                padding: '60px 80px',
                minHeight: 'calc(100vh - 4rem)',
                boxShadow: '0 0 60px rgba(0,0,0,0.6)',
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontSize: 16,
                lineHeight: 1.8,
                color: '#1A1410',
              }}
            >
              <EditorContent editor={editor} />
            </div>
          </div>

          {/* Streaming overlay */}
          {streaming && (
            <div
              className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-6 py-4"
              style={{ background: 'linear-gradient(transparent, rgba(240,244,250,0.95))', height: 80 }}
            >
              <div className="flex items-center gap-3">
                <Sparkles style={{ width: 14, height: 14, color: '#2563eb' }} className="animate-pulse" />
                <span className="font-sans text-sm" style={{ color: '#2563eb' }}>{streamingLabel}</span>
                <span className="flex gap-0.5 ml-1">
                  {[0, 1, 2].map(i => (
                    <span
                      key={i}
                      className="w-1 h-1 rounded-full"
                      style={{ background: '#2563eb', animation: `bounce 1.2s ${i * 0.2}s ease-in-out infinite` }}
                    />
                  ))}
                </span>
              </div>
              {onStopStreaming && (
                <button
                  onClick={onStopStreaming}
                  className="px-3 py-1.5 rounded-lg font-sans text-xs text-acdyon-textMuted hover:text-acdyon-text transition-colors"
                  style={{ border: '1px solid #DDE4EF' }}
                >
                  Stop generating
                </button>
              )}
            </div>
          )}
        </div>

        {/* Right panel */}
        <RightPanel
          documentId={documentId}
          wordCount={wordCount}
          targetWords={targetWords}
          saving={saving}
          lastSaved={lastSaved}
          docTitle={docTitle}
        />
      </div>

      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); opacity: 0.6; }
          50% { transform: translateY(-4px); opacity: 1; }
        }
      `}</style>
    </>
  )
})

export default DocumentEditor
